import ButtonComponent from 'components/button';
import type * as PI from 'interfaces/project';
import {
	LOGO_TOO_COMPLEX_ERROR_CODES,
	PHOTO_DETECTED_FOR_LIMITED_COLOR_LOGO_ERROR_CODE,
} from 'interfaces/project';
import { UIModule } from 'store';
import { componentSlot } from 'store/ui';

interface LimitedColorLogoPhotoErrorDialogOptions {
	onClose?: () => void;
}

interface PhotoModelWithCode extends Exclude<PI.PhotoModel, '_error'> {
	_error: (
		Exclude<PI.PhotoProcessingError, 'code'>
		& {
			code: PI.LogoTooComplexErrorCode;
		}
	);
}

let activeClose: (() => void) | undefined;
let activeCloseDialog: (() => void) | undefined;
let activeCloseCallbacks: Array<() => void> = [];
let activeGeneration = 0;

export function isLimitedColorLogoPhotoError(photoModel: PI.PhotoModel): boolean {
	return photoModel._error?.code === PHOTO_DETECTED_FOR_LIMITED_COLOR_LOGO_ERROR_CODE;
}

const LOGO_TOO_COMPLEX_ERROR_CODE_SET = new Set<string>(LOGO_TOO_COMPLEX_ERROR_CODES);

/**
 * Maps each too-complex error code to its i18n message sub-key under
 * `dialogs.logoTooComplex.messages`.
 */
const LOGO_TOO_COMPLEX_MESSAGE_KEYS: Record<PI.LogoTooComplexErrorCode, string> = {
	LOGO_IMAGE_DIMENSIONS_TOO_LARGE: 'imageDimensionsTooLarge',
	LOGO_IMAGE_TOO_LARGE: 'imageTooLarge',
	LOGO_TOO_MANY_CLIP_PATHS: 'tooManyClipPaths',
	LOGO_TOO_MANY_GRADIENT_STOPS: 'tooManyGradientStops',
	LOGO_TOO_DETAILED: 'tooDetailed',
	LOGO_TOO_COMPLEX: 'generic',
};

export function isLogoTooComplexError(photoModel: PI.PhotoModel): photoModel is PhotoModelWithCode {
	return (
		!!photoModel._error?.code
		&& LOGO_TOO_COMPLEX_ERROR_CODE_SET.has(photoModel._error.code)
	);
}

/**
 * Tear down the active limited-color logo error dialog without running its
 * queued onClose callbacks.
 *
 * The dialog is a module-global singleton: re-entrant
 * {@link openLimitedColorLogoPhotoErrorDialog} calls reuse the existing
 * `activeClose`. If the dialog is destroyed by other means (e.g. a route
 * change or a parent dialog stack teardown) the singleton is never cleared,
 * which leaks `activeClose` and suppresses every future error dialog. Callers
 * that own the dialog's lifecycle should invoke this on teardown to release
 * the singleton. Unlike `close`, this does NOT fire the queued onClose
 * callbacks, because a teardown is not a user-initiated dismissal.
 */
export function resetLimitedColorLogoPhotoErrorDialog(): void {
	if (!activeClose) {
		return;
	}

	const closeDialog = activeCloseDialog;
	activeGeneration += 1;
	activeClose = undefined;
	activeCloseDialog = undefined;
	activeCloseCallbacks = [];
	closeDialog?.();
}

interface ProcessingErrorDialogMessageKeys {
	title: string;
	message: string;
	accept: string;
}

/**
 * Opens a single-button error dialog for a failed-to-process logo. The dialog is
 * a module-global singleton (see {@link resetLimitedColorLogoPhotoErrorDialog}),
 * so during a batch upload the first error wins and later errors reuse it rather
 * than stacking.
 */
function openProcessingErrorDialog(
	messageKeys: ProcessingErrorDialogMessageKeys,
	options?: LimitedColorLogoPhotoErrorDialogOptions,
): () => void {
	if (options?.onClose) {
		activeCloseCallbacks.push(options.onClose);
	}

	if (activeClose) {
		return activeClose;
	}

	let closed = false;
	const generation = activeGeneration;
	const close = (): void => {
		if (closed || generation !== activeGeneration) {
			return;
		}

		closed = true;
		const closeDialog = activeCloseDialog;
		const closeCallbacks = activeCloseCallbacks.slice(0);
		activeGeneration += 1;
		activeClose = undefined;
		activeCloseDialog = undefined;
		activeCloseCallbacks = [];
		closeDialog?.();
		closeCallbacks.forEach((callback) => callback());
	};

	activeClose = close;
	activeCloseDialog = UIModule.openDialog({
		header: componentSlot({
			slot: window.App.router.$t(messageKeys.title),
		}),
		body: componentSlot({
			slot: window.App.router.$t(messageKeys.message),
		}),
		footer: componentSlot({
			tag: ButtonComponent,
			props: {
				class: 'grow',
				onClick: close,
			},
			slot: window.App.router.$t(messageKeys.accept),
		}),
		props: {
			onClose: close,
			'onClick:outside': close,
			onClick: (event) => {
				event.preventDefault();
			},
		},
	});

	return close;
}

export function openLimitedColorLogoPhotoErrorDialog(options?: LimitedColorLogoPhotoErrorDialogOptions): () => void {
	return openProcessingErrorDialog(
		{
			title: 'dialogs.photoNotSupportedLimitedColors.title',
			message: 'dialogs.photoNotSupportedLimitedColors.message',
			accept: 'dialogs.photoNotSupportedLimitedColors.buttons.accept',
		},
		options,
	);
}

/**
 * Opens the "logo too complex" dialog with a message specific to the classified
 * reason ({@link PI.LogoTooComplexErrorCode}).
 */
export function openLogoTooComplexErrorDialog(
	code: PI.LogoTooComplexErrorCode,
	options?: LimitedColorLogoPhotoErrorDialogOptions,
): () => void {
	const messageKey = LOGO_TOO_COMPLEX_MESSAGE_KEYS[code];

	return openProcessingErrorDialog(
		{
			title: 'dialogs.logoTooComplex.title',
			message: `dialogs.logoTooComplex.messages.${messageKey}`,
			accept: 'dialogs.logoTooComplex.buttons.accept',
		},
		options,
	);
}
